/* eslint-disable react/prop-types, react/destructuring-assignment */
import { fetchAuthSession } from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react'
import UrlPattern from 'url-pattern'
import awsConfig from '../../app/pages/auth/aws-exports'

Amplify.configure(awsConfig)

const DEFAULT_DOCUMENT_TITLE = 'Augmentt'
const DOCUMENT_TITLE_SUFFIX = ' - Augmentt'

/**
 * Sync the browser tab title with the active MenuConfig page title.
 * Matches SubHeader; covers routes that never set document.title themselves.
 */
function syncDocumentTitle(title) {
  if (title) {
    document.title = `${title}${DOCUMENT_TITLE_SUFFIX}`
    return
  }

  document.title = DEFAULT_DOCUMENT_TITLE
}

/**
 * Both context used to create inside react `redux`-like global state managed
 * entirely by react.
 *
 * @see https://kentcdodds.com/blog/how-to-use-react-context-effectively
 */

const LayoutContext = {
  /**
   * Stores layout state, can be consumed globally.
   */
  State: createContext(null),

  /**
   * Stores `dispatch` function to update layout state, intended to be internal.
   */
  Dispatch: createContext(null),
}

/**
 * Layout action types, used to filter out dispatched actions.
 */
const actionTypes = {
  /**
   * Initializes layout state from provided `{ pathname, menuConfig }` action
   * payload.
   */
  INIT: 'INIT',

  /**
   * Updates current subheader from provided `{ title }` action payload.
   */
  SET_SUBHEADER: 'SET_SUBHEADER',

  /**
   * Controls splash screen visibility.
   */
  SHOW_SPLASH_SCREEN: 'SHOW_SPLASH_SCREEN',
  HIDE_SPLASH_SCREEN: 'HIDE_SPLASH_SCREEN',
}

/**
 * Recursively runs over `items` to find `pageConfig` of `currentPage`.
 *
 * Returns `undefined` if there are no matches.
 */
function findPageConfig(currentPage, items, breadcrumbs) {
  // Ignore non array `items`.
  if (!items || !Array.isArray(items)) {
    return
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const item of items) {
    if (item.page) {
      const pattern = new UrlPattern(item.page)

      // Return `item` if it's `page` matches `currentPage`
      if (pattern.match(currentPage)) {
        return item // eslint-disable-line consistent-return
      }
    }

    // Try to `pageConfig` in `item.submenu` if it is defined.
    if (item.submenu) {
      const pageConfig = findPageConfig(currentPage, item.submenu, breadcrumbs)
      if (pageConfig) {
        breadcrumbs.push(item)
        return pageConfig // eslint-disable-line consistent-return
      }
    }
  }
}

/**
 * Used to lazily create initial layout state.
 */
function init({ pathname, menuConfig }) {
  let currentPage = pathname.slice(1)
  const regex = /\d$/ // \d matches any digit (0-9) at the end
  const containsNumber = regex.test(currentPage)
  if (containsNumber) {
    // eslint-disable-next-line prefer-destructuring
    currentPage = currentPage.split('/').slice(0, -1).join('/')
  }
  const breadcrumbs = []

  const pageConfig = findPageConfig(currentPage, menuConfig.aside.items, breadcrumbs)

  breadcrumbs.reverse()
  const state = {
    subheader: {
      title: menuConfig.aside.items[0].title,
      beta: menuConfig.aside.items[0].beta,
      legacy: menuConfig.aside.items[0].badge === 'legacy',
      breadcrumb: [],
      description: '',
      pageMatched: false,
    },
    splashScreen: { refs: {} },
  }
  if (pageConfig) {
    breadcrumbs.push(pageConfig)
    state.subheader.title = pageConfig.title
    state.subheader.beta = pageConfig.beta
    state.subheader.legacy = pageConfig.badge === 'legacy'
    state.subheader.breadcrumb = breadcrumbs
    state.subheader.pageMatched = true
  }

  return state
}

function reducer(state, { type, payload }) {
  if (type === actionTypes.INIT) {
    const nextState = init(payload)

    // Update only subheader.
    return { ...state, subheader: nextState.subheader }
  }

  if (type === actionTypes.SET_SUBHEADER) {
    return {
      ...state,
      subheader: {
        ...payload,
        // Keep route-match flag so document.title sync is not cleared by title overrides.
        pageMatched: state.subheader.pageMatched,
      },
    }
  }

  if (type === actionTypes.SHOW_SPLASH_SCREEN) {
    return {
      ...state,
      splashScreen: {
        ...state.splashScreen,
        refs: { ...state.splashScreen.refs, [payload.id]: true },
      },
    }
  }

  if (type === actionTypes.HIDE_SPLASH_SCREEN) {
    const { [payload.id]: skip, ...nextRefs } = state.splashScreen.refs

    return {
      ...state,
      splashScreen: { ...state.splashScreen, refs: nextRefs },
    }
  }

  return state
}

/**
 * Getting current user session, refreshing token if expired
 */
async function checkSession() {
  try {
    await fetchAuthSession()
  } catch (e) {
    // console.log(e);
  }
}

/**
 * Creates layout reducer and provides it's `state` and ` dispatch`.
 */
export function LayoutContextProvider({ history, children, menuConfig }) {
  const [state, dispatch] = useReducer(
    reducer,
    { menuConfig, pathname: history.location.pathname },
    // See https://reactjs.org/docs/hooks-reference.html#lazy-initialization
    init,
  )

  // Subscribe to history changes and reinitialize on each change.
  useEffect(
    () => {
      history.listen(({ pathname }) => {
        checkSession()
        dispatch({
          type: actionTypes.INIT,
          payload: { pathname, menuConfig },
        })
      })
    },
    /**
     * Passing `history` and `menuConfig` to `deps` ensures that `useEffect`
     * will cleanup current `history` listener and will dispatch `INIT`
     * with `menuConfig` reference from current render.
     *
     * @see https://reactjs.org/docs/hooks-reference.html#conditionally-firing-an-effect
     */

    [history, menuConfig],
  )

  const { refs: splashScreenRefs } = state.splashScreen
  const splashScreenVisible = useMemo(() => Object.keys(splashScreenRefs).length > 0, [splashScreenRefs])
  const { title: subheaderTitle, pageMatched } = state.subheader
  const lastSyncedTitleRef = useRef(null)

  useEffect(() => {
    if (pageMatched) {
      syncDocumentTitle(subheaderTitle)
      lastSyncedTitleRef.current = subheaderTitle ? `${subheaderTitle}${DOCUMENT_TITLE_SUFFIX}` : DEFAULT_DOCUMENT_TITLE
      return
    }

    // Unmatched routes (auth, /error, etc.): clear only if the previous MenuConfig title is still
    // stuck. Pages that set their own document.title during render are left alone.
    if (lastSyncedTitleRef.current && document.title === lastSyncedTitleRef.current) {
      syncDocumentTitle()
    }
    lastSyncedTitleRef.current = null
  }, [subheaderTitle, pageMatched])

  useEffect(() => {
    const splashScreen = document.getElementById('splash-screen')
    const { body } = document

    if (splashScreenVisible) {
      splashScreen.classList.remove('hidden')
      body.classList.add('loading')

      return () => {
        splashScreen.classList.add('hidden')
      }
    }

    const timeout = setTimeout(() => {
      splashScreen.classList.add('hidden')
      body.classList.remove('loading')
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [splashScreenVisible])

  // Pass state and dispatch to it's contexts.
  return (
    <LayoutContext.State.Provider value={state}>
      <LayoutContext.Dispatch.Provider value={dispatch}>{children}</LayoutContext.Dispatch.Provider>
    </LayoutContext.State.Provider>
  )
}

/**
 * Used to access latest layout context state.
 *
 * @example
 *
 * export function Subheader() {
 *   return (
 *     <LayoutContextConsumer>
 *       {({ subheader: { title } }) => <h1>{title}</h1>}
 *     </LayoutContextConsumer>
 *   );
 * }
 */
export const LayoutContextConsumer = LayoutContext.State.Consumer

/**
 * Hook to access latest layout context state.
 *
 * @example
 *
 * export function Subheader() {
 *   const { subheader: { title } } = useLayoutContext();
 *
 *   return <h1>{title}</h1>;
 * }
 */
export function useLayoutContext() {
  const context = useContext(LayoutContext.State)

  if (!context) {
    throw new Error('')
  }

  return context
}

/**
 * Used to override layout subheader state.
 */
export function LayoutSubheader({ title, breadcrumb, description }) {
  const dispatch = useContext(LayoutContext.Dispatch)

  useEffect(() => {
    dispatch({
      type: actionTypes.SET_SUBHEADER,
      payload: { title, breadcrumb, description },
    })
  }, [dispatch, title, breadcrumb, description])

  return null
}

export function LayoutSplashScreen({ visible = false }) {
  const dispatch = useContext(LayoutContext.Dispatch)

  useEffect(() => {
    if (!visible) {
      return
    }

    const id = Math.random()

    dispatch({ type: actionTypes.SHOW_SPLASH_SCREEN, payload: { id } })

    // eslint-disable-next-line consistent-return
    return () => {
      dispatch({ type: actionTypes.HIDE_SPLASH_SCREEN, payload: { id } })
    }
  }, [visible, dispatch])

  return null
}
